import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { Icon } from "./lib/icons";
import { defaultRoles, getRoleById, loadActiveRoleId, loadRoles, ROLES_EVENT, syncRolesFromServer } from "./lib/roles";
import { API_URL, apiGet, apiPatch, clearAuthSession, getAuthRoles, getAuthToken } from "./lib/api";
import { syncBrandColor } from "./lib/theme";
import { LOGO_EVENT, ensureBrandingLoaded, getLogoDataUrl, refreshBranding } from "./lib/branding";
import { SOCKET_TRANSPORTS } from "./lib/realtime";

const InboxPage = lazy(() => import("./pages/InboxPage"));
const PipelinePage = lazy(() => import("./pages/PipelinePage"));
const CampaignsPage = lazy(() => import("./pages/CampaignsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupTrialPage = lazy(() => import("./pages/SignupTrialPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ContactsPage = lazy(() => import("./pages/ContactsPage"));
const InternalChatPage = lazy(() => import("./pages/InternalChatPage"));
const SchedulePage = lazy(() => import("./pages/SchedulePage"));
const ActiveConversationsPage = lazy(() => import("./pages/ActiveConversationsPage"));
const FlowBuilderPage = lazy(() => import("./pages/FlowBuilderPage"));

const navLinkClass = ({ isActive }: { isActive: boolean }) => (isActive ? "active" : undefined);

const navLinkWithClass =
  (extra: string) =>
  ({ isActive }: { isActive: boolean }) => {
    const base = navLinkClass({ isActive });
    return base ? `${base} ${extra}` : extra;
  };

const getPreferredSidebarState = () => {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("omni:sidebar");
  if (!stored) return true;
  return stored === "collapsed";
};

const getStoredTheme = () => {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem("omni:theme");
  return stored === "light" || stored === "dark" ? stored : null;
};

const getSystemTheme = (): "light" | "dark" => {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
};

const getInitialTheme = (): { theme: "light" | "dark"; source: "stored" | "system" } => {
  const stored = getStoredTheme();
  if (stored) return { theme: stored, source: "stored" as const };
  return { theme: getSystemTheme(), source: "system" as const };
};

const getAdminMode = () => {
  const roles = getAuthRoles();
  return roles.includes("TENANT_ADMIN") || roles.includes("SUPERADMIN");
};

const getCurrentUserIdFromToken = () => {
  if (typeof window === "undefined" || typeof window.atob !== "function") return null;
  const token = getAuthToken();
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const payload = JSON.parse(window.atob(padded)) as { sub?: unknown };
    return typeof payload?.sub === "string" && payload.sub.trim().length ? payload.sub : null;
  } catch {
    return null;
  }
};

type NotificationItem = {
  id: string;
  title: string;
  detail?: string;
  tone?: "info" | "warning" | "danger";
  actionLabel?: string;
  onAction?: () => void;
};

type AgentAvailabilityStatus = "available" | "paused" | "offline";

const availabilityLabelMap: Record<AgentAvailabilityStatus, string> = {
  available: "Online",
  paused: "Ausente",
  offline: "Offline",
};

const availabilityReasonTitleMap: Record<Exclude<AgentAvailabilityStatus, "available">, string> = {
  paused: "Motivo da ausência",
  offline: "Motivo do offline",
};

const availabilityReasonPlaceholderMap: Record<Exclude<AgentAvailabilityStatus, "available">, string> = {
  paused: "Ex: horário de almoço, reunião, pausa rápida...",
  offline: "Ex: fim do expediente, pausa longa, desconexão temporária...",
};

function PageLoader() {
  return (
    <div className="page-loader" role="status" aria-live="polite">
      <div className="page-loader-spinner" aria-hidden="true" />
      <span>Carregando...</span>
    </div>
  );
}

const getDaysRemaining = (isoDate?: string | null) => {
  if (!isoDate) return null;
  const target = new Date(isoDate).getTime();
  if (!Number.isFinite(target)) return null;
  const diff = target - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

function RequireAuth({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const token = getAuthToken();
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function AppShell({ theme, onToggleTheme }: { theme: "light" | "dark"; onToggleTheme: () => void }) {
  const inactivityLimitMs = 10 * 60 * 1000;
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isSidebarCollapsed] = useState(true);
  const [isAdminMode, setIsAdminMode] = useState(getAdminMode);
  const [currentUser, setCurrentUser] = useState<{ name?: string; roles?: string[] } | null>(null);
  const [currentUserAvailability, setCurrentUserAvailability] = useState<AgentAvailabilityStatus>("available");
  const [availabilitySaveState, setAvailabilitySaveState] = useState<"idle" | "saving" | "error">("idle");
  const [availabilityModalStatus, setAvailabilityModalStatus] = useState<Exclude<AgentAvailabilityStatus, "available"> | null>(null);
  const [availabilityReason, setAvailabilityReason] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(() => getLogoDataUrl());
  const [rolePermissions, setRolePermissions] = useState(defaultRoles[0]?.permissions ?? {});
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [internalChatUnreadCount, setInternalChatUnreadCount] = useState(0);
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const lastActivityAtRef = useRef(Date.now());
  const availabilityRef = useRef<AgentAvailabilityStatus>("available");
  const availabilitySaveStateRef = useRef<"idle" | "saving" | "error">("idle");
  const autoOfflineRef = useRef(false);
  const internalChatAudioContextRef = useRef<AudioContext | null>(null);
  const lastInternalChatToneAtRef = useRef(0);

  const refreshInternalChatUnread = useCallback(() => {
    apiGet<{ total?: number }>("/internal-chat/unread")
      .then((data) => setInternalChatUnreadCount(Number(data?.total) || 0))
      .catch(() => null);
  }, []);

  const playInternalChatTone = useCallback(() => {
    if (typeof window === "undefined") return;
    const now = Date.now();
    if (now - lastInternalChatToneAtRef.current < 900) return;
    lastInternalChatToneAtRef.current = now;
    try {
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextCtor) return;
      const context = internalChatAudioContextRef.current ?? new AudioContextCtor();
      internalChatAudioContextRef.current = context;
      if (context.state === "suspended") {
        void context.resume();
      }
      const createBeep = (startAt: number, frequency: number) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(frequency, startAt);
        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.exponentialRampToValueAtTime(0.2, startAt + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.22);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(startAt);
        oscillator.stop(startAt + 0.24);
      };
      createBeep(context.currentTime, 920);
      createBeep(context.currentTime + 0.12, 1080);
    } catch {
      // Silent fallback.
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (showUserMenu && userMenuRef.current && target && !userMenuRef.current.contains(target)) {
        setShowUserMenu(false);
      }
      if (showNotifications && notificationsRef.current && target && !notificationsRef.current.contains(target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu, showNotifications]);

  useEffect(() => {
    const onStorage = () => setIsAdminMode(getAdminMode());
    if (typeof window !== "undefined") {
      window.addEventListener("storage", onStorage);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", onStorage);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = getAuthToken();
    if (!token) return;
    let active = true;
    apiGet<{ name?: string; displayName?: string | null; roles?: string[]; availabilityStatus?: AgentAvailabilityStatus }>("/auth/me")
      .then((data) => {
        if (!active) return;
        setCurrentUser({ name: data?.displayName?.trim() || data?.name, roles: data?.roles });
        if (data?.availabilityStatus === "paused" || data?.availabilityStatus === "offline") {
          setCurrentUserAvailability(data.availabilityStatus);
        } else {
          setCurrentUserAvailability("available");
        }
      })
      .catch(() => null);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    availabilityRef.current = currentUserAvailability;
  }, [currentUserAvailability]);

  useEffect(() => {
    availabilitySaveStateRef.current = availabilitySaveState;
  }, [availabilitySaveState]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = getAuthToken();
    if (!token) return;
    let active = true;

    const loadNotifications = async () => {
      try {
        const data = await apiGet<{ tenant?: { isTrial?: boolean; trialEnd?: string | null } }>("/billing/subscription");
        if (!active) return;
        const items: NotificationItem[] = [];
        const tenant = data?.tenant;
        if (tenant?.isTrial && tenant?.trialEnd) {
          const daysRemaining = getDaysRemaining(tenant.trialEnd);
          if (daysRemaining !== null) {
            const plural = daysRemaining === 1 ? "dia" : "dias";
            const tone = daysRemaining <= 2 ? "danger" : daysRemaining <= 5 ? "warning" : "info";
            items.push({
              id: "trial",
              title: "Periodo de teste",
              detail: `Faltam ${daysRemaining} ${plural} para terminar o periodo de teste.`,
              tone,
              actionLabel: "Ver assinatura",
              onAction: () => navigate("/settings", { state: { sectionId: "sales-payments" } }),
            });
          }
        }
        setNotifications(items);
      } catch {
        if (!active) return;
        setNotifications([]);
      }
    };

    void loadNotifications();
    const interval = window.setInterval(loadNotifications, 300_000);
    const handleFocus = () => void loadNotifications();
    window.addEventListener("focus", handleFocus);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [navigate]);

  useEffect(() => {
    refreshInternalChatUnread();
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const unlockAudio = () => {
      try {
        const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextCtor) return;
        const context = internalChatAudioContextRef.current ?? new AudioContextCtor();
        internalChatAudioContextRef.current = context;
        if (context.state === "suspended") {
          void context.resume();
        }
      } catch {
        // Keep silent if audio is unavailable.
      }
    };
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart"];
    events.forEach((eventName) => window.addEventListener(eventName, unlockAudio, { passive: true }));
    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, unlockAudio));
    };
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    const tenantId = typeof window !== "undefined" ? window.localStorage.getItem("omni:tenantId") : null;
    const currentUserId = getCurrentUserIdFromToken();
    const socket = io(`${API_URL}/agent`, {
      auth: { token, tenantId },
      transports: SOCKET_TRANSPORTS,
    });

    socket.on("internal_chat_message", (event: { actorUserId?: string; contentType?: string; mutedUserIds?: string[]; mentionUserIds?: string[] }) => {
      refreshInternalChatUnread();
      const actorUserId = String(event?.actorUserId ?? "").trim();
      if (!actorUserId || (currentUserId && actorUserId === currentUserId)) return;
      if (event?.contentType === "system") return;
      const chatVisible = location.pathname.startsWith("/internal-chat") && !document.hidden;
      if (chatVisible) return;
      const muted = Boolean(currentUserId && event?.mutedUserIds?.includes(currentUserId));
      const mentioned = Boolean(currentUserId && event?.mentionUserIds?.includes(currentUserId));
      if (muted && !mentioned) return;
      playInternalChatTone();
    });
    socket.on("internal_chat_read", refreshInternalChatUnread);
    socket.on("internal_chat_thread", refreshInternalChatUnread);

    return () => {
      socket.disconnect();
    };
  }, [location.pathname, playInternalChatTone, refreshInternalChatUnread]);

  useEffect(() => {
    const refreshPermissions = () => {
      const roles = loadRoles();
      const activeRoleId = loadActiveRoleId();
      setRolePermissions(getRoleById(roles, activeRoleId)?.permissions ?? defaultRoles[0]?.permissions ?? {});
    };
    refreshPermissions();
    syncRolesFromServer().catch(() => undefined);
    syncBrandColor().catch(() => undefined);
    window.addEventListener(ROLES_EVENT, refreshPermissions);
    window.addEventListener("storage", refreshPermissions);
    return () => {
      window.removeEventListener(ROLES_EVENT, refreshPermissions);
      window.removeEventListener("storage", refreshPermissions);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleLogoChange = () => setLogoDataUrl(getLogoDataUrl());
    ensureBrandingLoaded()
      .then(() => handleLogoChange())
      .catch(() => {
        // Keep cached logo if the request fails.
      });
    const handleFocus = () => {
      refreshBranding().then(handleLogoChange).catch(() => null);
    };
    const interval = window.setInterval(handleFocus, 300_000);
    window.addEventListener("storage", handleLogoChange);
    window.addEventListener(LOGO_EVENT, handleLogoChange);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("storage", handleLogoChange);
      window.removeEventListener(LOGO_EVENT, handleLogoChange);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    clearAuthSession();
    setIsAdminMode(getAdminMode());
    setCurrentUser(null);
    setShowUserMenu(false);
    navigate("/login", { replace: true });
  };

  const handleAvailabilityChange = useCallback(
    async (
      status: AgentAvailabilityStatus,
      source: "manual" | "auto_inactivity" | "auto_resume" = "manual",
      reason?: string
    ) => {
      if (availabilitySaveStateRef.current === "saving") return;
      const previous = availabilityRef.current;
      const previousAutoOffline = autoOfflineRef.current;
      lastActivityAtRef.current = Date.now();
      autoOfflineRef.current = source === "auto_inactivity" && status === "offline";
      setCurrentUserAvailability(status);
      setAvailabilitySaveState("saving");
      try {
        await apiPatch("/users/me/availability", { status, source, reason });
        setAvailabilitySaveState("idle");
      } catch {
        autoOfflineRef.current = previousAutoOffline;
        setCurrentUserAvailability(previous);
        setAvailabilitySaveState("error");
        window.setTimeout(() => setAvailabilitySaveState("idle"), 2200);
      }
    },
    []
  );

  const cycleAvailability = useCallback(() => {
    if (availabilitySaveStateRef.current === "saving") return;
    const nextStatus: AgentAvailabilityStatus = availabilityRef.current === "available" ? "paused" : "available";
    if (nextStatus === "paused") {
      setAvailabilityReason("");
      setAvailabilityModalStatus(nextStatus);
      return;
    }
    void handleAvailabilityChange(nextStatus);
  }, [handleAvailabilityChange]);

  const submitAvailabilityReason = useCallback(
    (statusOverride?: Exclude<AgentAvailabilityStatus, "available">) => {
      if (!availabilityModalStatus) return;
      const nextStatus = statusOverride ?? availabilityModalStatus;
      const nextReason = availabilityReason.trim();
      setAvailabilityModalStatus(null);
      setAvailabilityReason("");
      setShowUserMenu(false);
      void handleAvailabilityChange(nextStatus, "manual", nextReason);
    },
    [availabilityModalStatus, availabilityReason, handleAvailabilityChange]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const markActivity = () => {
      lastActivityAtRef.current = Date.now();
      if (document.visibilityState === "hidden") return;
      if (availabilitySaveStateRef.current === "saving") return;
      if (!autoOfflineRef.current) return;
      if (availabilityRef.current !== "offline") return;
      void handleAvailabilityChange("available", "auto_resume");
    };

    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart", "mousemove", "focus"];
    events.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));
    document.addEventListener("visibilitychange", markActivity);

    const interval = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      if (availabilityRef.current === "offline") return;
      if (availabilitySaveStateRef.current === "saving") return;
      if (Date.now() - lastActivityAtRef.current < inactivityLimitMs) return;
      void handleAvailabilityChange("offline", "auto_inactivity");
    }, 30000);

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, markActivity));
      document.removeEventListener("visibilitychange", markActivity);
      window.clearInterval(interval);
    };
  }, [handleAvailabilityChange]);

  const userName = currentUser?.name?.trim() || "Agente";
  const roleLabel = (() => {
    const roles = currentUser?.roles ?? getAuthRoles();
    if (roles.includes("TENANT_ADMIN")) return "Admin";
    if (roles.includes("SUPERVISOR")) return "Supervisor";
    return "Agente";
  })();
  const initials = userName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <div className={`app-shell ${isSidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <aside className="sidebar">
        <div className="brand-row">
          <div className="brand">
            <div className={`brand-mark ${logoDataUrl ? "has-logo" : ""}`}>
              {logoDataUrl ? <img src={logoDataUrl} alt="Logotipo OmniDesk" /> : "OD"}
            </div>
            <div className="brand-text">
              <h1>OmniDesk</h1>
              <p>Console do Agente</p>
            </div>
          </div>
        </div>
        <nav className="nav">
          {rolePermissions.nav_inbox ? (
            <NavLink to="/" end className={navLinkClass}>
              <span className="nav-icon">
                <Icon name="inbox" />
              </span>
              <span className="nav-text">Atendimento</span>
            </NavLink>
          ) : null}
          {rolePermissions.nav_dashboard ? (
            <NavLink to="/pipeline" className={navLinkWithClass("nav-item--dashboard")}>
              <span className="nav-icon">
                <Icon name="activity" />
              </span>
              <span className="nav-text">Dashboard</span>
            </NavLink>
          ) : null}
          {rolePermissions.nav_campaigns ? (
            <NavLink to="/campaigns" className={navLinkWithClass("nav-item--campaigns")}>
              <span className="nav-icon">
                <Icon name="sparkles" />
              </span>
              <span className="nav-text">Campanhas</span>
            </NavLink>
          ) : null}
          {rolePermissions.nav_contacts ? (
            <NavLink to="/contacts" className={navLinkClass}>
              <span className="nav-icon">
                <Icon name="users" />
              </span>
              <span className="nav-text">Contatos</span>
            </NavLink>
          ) : null}
          {rolePermissions.nav_internal_chat ? (
            <NavLink to="/internal-chat" className={navLinkClass}>
              <span className="nav-icon">
                <Icon name="message" />
              </span>
              <span className="nav-text">Chat Interno</span>
              {internalChatUnreadCount > 0 ? (
                <span className="nav-inline-badge">{internalChatUnreadCount > 99 ? "99+" : internalChatUnreadCount}</span>
              ) : null}
            </NavLink>
          ) : null}
          {rolePermissions.nav_schedule ? (
            <NavLink to="/schedule" className={navLinkWithClass("nav-item--schedule")}>
              <span className="nav-icon">
                <Icon name="calendar" />
              </span>
              <span className="nav-text">Agendamento</span>
            </NavLink>
          ) : null}
          {isAdminMode && rolePermissions.nav_active_conversations ? (
            <NavLink to="/active-conversations" className={navLinkWithClass("nav-item--active-conversations")}>
              <span className="nav-icon">
                <Icon name="activity" />
              </span>
              <span className="nav-text">Conversas ativas</span>
            </NavLink>
          ) : null}
          {(isAdminMode || getAuthRoles().includes("SUPERVISOR")) && rolePermissions.nav_chatbot_flow_builder ? (
            <NavLink to="/chatbot/flows" className={navLinkWithClass("nav-item--flow-builder")}>
              <span className="nav-icon">
                <Icon name="zap" />
              </span>
              <span className="nav-text">Construtor de Fluxo</span>
            </NavLink>
          ) : null}
        </nav>
        <div className="sidebar-footer" ref={userMenuRef}>
          <div className="sidebar-footer-notifications" ref={notificationsRef}>
            <button
              className={`notification-button ${showNotifications ? "active" : ""}`}
              type="button"
              onClick={() => setShowNotifications((prev) => !prev)}
              aria-label="Notificacoes"
            >
              <Icon name="bell" />
              {notifications.length ? <span className="notification-badge">{notifications.length}</span> : null}
            </button>
            {showNotifications ? (
              <div className="notification-menu notification-menu--footer">
                <div className="notification-header">
                  <strong>Notificacoes</strong>
                  <span>{notifications.length} aviso(s)</span>
                </div>
                {notifications.length ? (
                  <div className="notification-list">
                    {notifications.map((item) => (
                      <div key={item.id} className={`notification-item ${item.tone ?? "info"}`}>
                        <div className="notification-title">{item.title}</div>
                        {item.detail ? <div className="notification-detail">{item.detail}</div> : null}
                        {item.actionLabel && item.onAction ? (
                          <button className="notification-cta" type="button" onClick={item.onAction}>
                            {item.actionLabel}
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="notification-empty">Sem avisos do super admin.</div>
                )}
              </div>
            ) : null}
          </div>
          <button
            className="user-menu-trigger"
            type="button"
            onClick={() =>
              setShowUserMenu((prev) => {
                return !prev;
              })
            }
            aria-label="Menu do usuário"
            aria-expanded={showUserMenu}
          >
            <div className="user-avatar-wrap">
              <div className="user-avatar">{initials || "U"}</div>
              <span className={`user-avatar-status ${currentUserAvailability}`} aria-hidden="true" />
            </div>
            <div className="user-info">
              <span>{roleLabel}</span>
              <strong>{userName}</strong>
            </div>
            <span className="chevron">▼</span>
          </button>
          {showUserMenu ? (
            <div className="user-menu">
              <button className={`menu-item ${theme === "dark" ? "active" : ""}`} type="button" onClick={onToggleTheme}>
                <span className="menu-item-icon" aria-hidden="true">
                  <Icon name={theme === "dark" ? "sun" : "moon"} />
                </span>
                <span>{theme === "dark" ? "Modo claro" : "Modo escuro"}</span>
              </button>
              <button
                className="menu-item"
                type="button"
                onClick={() => {
                  setShowUserMenu(false);
                  navigate("/settings", { state: { sectionId: "account", anchor: "settings-account-password" } });
                }}
              >
                <span className="menu-item-icon" aria-hidden="true">
                  <Icon name="lock" />
                </span>
                <span>Alterar senha</span>
              </button>
              <button
                className="menu-item"
                type="button"
                onClick={() => {
                  setShowUserMenu(false);
                  navigate("/settings", { state: { sectionId: "account", anchor: "settings-account-profile" } });
                }}
              >
                <span className="menu-item-icon" aria-hidden="true">
                  <Icon name="user" />
                </span>
                <span>Dados pessoais</span>
              </button>
              <button
                className="menu-item"
                type="button"
                onClick={cycleAvailability}
                disabled={availabilitySaveState === "saving"}
              >
                <span className="menu-item-icon" aria-hidden="true">
                  <span className={`menu-status-dot ${currentUserAvailability}`} />
                  <Icon name="activity" />
                </span>
                <span>{`Status - ${availabilityLabelMap[currentUserAvailability]}`}</span>
              </button>
              {availabilitySaveState === "error" ? (
                <span className="menu-status-error">Nao foi possivel atualizar o status.</span>
              ) : null}
              <button
                className="menu-item"
                type="button"
                onClick={() => {
                  setShowUserMenu(false);
                  navigate("/settings", { state: { sectionId: "account" } });
                }}
              >
                <span className="menu-item-icon" aria-hidden="true">
                  <Icon name="keyboard" />
                </span>
                <span>Atalhos</span>
              </button>
              <button
                className="menu-item"
                type="button"
                onClick={() => {
                  setShowUserMenu(false);
                  navigate("/settings");
                }}
              >
                <span className="menu-item-icon" aria-hidden="true">
                  <Icon name="settings" />
                </span>
                <span>Configurações</span>
              </button>
              {isAdminMode || rolePermissions.settings_sales_payments ? (
                <button
                  className="menu-item"
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate("/settings", { state: { sectionId: "sales-payments" } });
                  }}
                >
                  <span className="menu-item-icon" aria-hidden="true">
                    <Icon name="card" />
                  </span>
                  <span>Assinaturas e pagamentos</span>
                </button>
              ) : null}
              <div className="menu-divider" />
              <button className="menu-item logout" type="button" onClick={handleLogout}>
                <span className="menu-item-icon" aria-hidden="true">
                  <Icon name="logout" />
                </span>
                <span>Sair</span>
              </button>
            </div>
          ) : null}
        </div>
      </aside>
      <div className="app-main">
        <main className="content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<InboxPage />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/pipeline" element={<PipelinePage />} />
              <Route path="/campaigns" element={<CampaignsPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/internal-chat" element={<InternalChatPage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/active-conversations" element={<ActiveConversationsPage />} />
              <Route path="/chatbot/flows" element={<FlowBuilderPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      {availabilityModalStatus ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setAvailabilityModalStatus(null)}>
          <div className="modal modal-compact" onClick={(event) => event.stopPropagation()}>
            <header>
              <h4>{availabilityReasonTitleMap[availabilityModalStatus]}</h4>
            </header>
            <div className="modal-body">
              <p className="modal-hint">
                Informe o motivo para registrar esse status e facilitar o acompanhamento operacional.
              </p>
              <textarea
                className="profile-textarea availability-reason-textarea"
                value={availabilityReason}
                onChange={(event) => setAvailabilityReason(event.target.value)}
                placeholder={availabilityReasonPlaceholderMap[availabilityModalStatus]}
                rows={4}
                autoFocus
              />
            </div>
            <footer className="modal-inline-actions">
              <button className="button ghost" type="button" onClick={() => setAvailabilityModalStatus(null)}>
                Cancelar
              </button>
              {availabilityModalStatus === "paused" ? (
                <button className="button secondary" type="button" onClick={() => submitAvailabilityReason("offline")}>
                  Marcar offline
                </button>
              ) : null}
              <button className="button primary" type="button" onClick={() => submitAvailabilityReason()}>
                Confirmar
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const [{ theme, source }, setThemeState] = useState<{ theme: "light" | "dark"; source: "stored" | "system" }>(
    getInitialTheme
  );
  const isLoginRoute =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/signup-trial") ||
    location.pathname.startsWith("/reset-password");
  const effectiveTheme = isLoginRoute ? getSystemTheme() : theme;

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = effectiveTheme;
    if (isLoginRoute) return;
    if (source === "stored") {
      window.localStorage.setItem("omni:theme", theme);
      return;
    }
    window.localStorage.removeItem("omni:theme");
  }, [effectiveTheme, isLoginRoute, source, theme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (isLoginRoute) {
      document.body.dataset.screen = "auth";
      return;
    }
    delete document.body.dataset.screen;
  }, [isLoginRoute]);

  useEffect(() => {
    if (typeof window === "undefined" || !isLoginRoute) return;
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const applySystemTheme = () => {
      if (typeof document === "undefined") return;
      document.documentElement.dataset.theme = getSystemTheme();
    };
    applySystemTheme();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", applySystemTheme);
      return () => media.removeEventListener("change", applySystemTheme);
    }
    media.addListener(applySystemTheme);
    return () => media.removeListener(applySystemTheme);
  }, [isLoginRoute]);

  useEffect(() => {
    if (typeof window === "undefined" || source !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const applySystemTheme = () =>
      setThemeState((prev) => (prev.source === "system" ? { theme: getSystemTheme(), source: "system" } : prev));
    applySystemTheme();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", applySystemTheme);
      return () => media.removeEventListener("change", applySystemTheme);
    }
    media.addListener(applySystemTheme);
    return () => media.removeListener(applySystemTheme);
  }, [source]);

  const toggleTheme = useMemo(
    () => () =>
      setThemeState((prev) => ({
        theme: prev.theme === "dark" ? "light" : "dark",
        source: "stored",
      })),
    []
  );

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup-trial" element={<SignupTrialPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <AppShell theme={theme} onToggleTheme={toggleTheme} />
            </RequireAuth>
          }
        />
      </Routes>
    </Suspense>
  );
}




